// /settings/pair — couple name + relationship_start editor (pair member only).
//
// W40.5. The page body used to be a bare `<div className="flex flex-col gap-4">`
// with no `PageContainer`, so its card ran edge-to-edge: measured on a 393px
// viewport at 0px against 16-18px on every other page. `DesktopShell` states the
// rule in a comment — 「Horizontal gutter is owned by `PageContainer`」 — and
// `PageContainer`'s own docstring says 「Wrap every page body in AppShell with
// this primitive」. This page was simply never migrated.
//
// It also renders through the shell's `<main>`, so `PageContainer` is asked for
// `as="div"`: its default is `as="main"`, and a second `<main>` inside the
// shell's is two landmarks for one page.

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { PageHero } from "@/components/primitives/PageHero";
import { ErrorState } from "@/components/primitives/ErrorState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormErrorBanner, FormField } from "@/components/auth/FormField";
import { PageContainer } from "@/components/layout";
import { ApiError, readPair, patchPair } from "@/lib/api/pair";
import { pairErrorMessage } from "@/lib/i18n/pair";
import { INFO_BANNER_PAIR_LOAD_FAILED } from "@/lib/i18n/errors";
import { patchPairSchema, type PatchPairInput } from "@/lib/validation/pair";
import { useToast } from "@/components/ui/toast";
import { queryKeys } from "@/lib/queries/keys";
import { useAuth } from "@/lib/auth/AuthProvider";

export function PairSettingsPage() {
  // Optional pairId from URL params; falls back to the session's active pair.
  const { pairId: paramPairId } = useParams<{ pairId?: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { push: pushToast } = useToast();
  const { pairId: authPairId, status: authStatus } = useAuth();

  // Use the auth-provided pairId when the URL parameter is absent.
  const initialPairId = paramPairId ?? authPairId ?? undefined;

  const pairQuery = useQuery({
    queryKey: queryKeys.pairs.detail(initialPairId ?? "active"),
    queryFn: () => readPair(initialPairId!),
    enabled: authStatus === "authenticated" && Boolean(initialPairId),
    retry: false,
  });

  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (pairQuery.data) {
      setName(pairQuery.data.pair.name);
      setStartDate(pairQuery.data.pair.relationship_start);
    }
  }, [pairQuery.data]);

  const updateMut = useMutation({
    mutationFn: (payload: PatchPairInput) => {
      const pairId = pairQuery.data?.pair.id;
      if (!pairId) throw new ApiError(404, "PAIR_NOT_FOUND", "找不到 Pair");
      return patchPair(pairId, payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.pairs.detail(pairQuery.data?.pair.id ?? ""),
      });
      pushToast({ variant: "success", message: "Pair 已更新" });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        pushToast({
          variant: "danger",
          message: pairErrorMessage(err.code, err.message),
        });
      }
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(null);
    setDateError(null);
    const parsed = patchPairSchema.safeParse({
      name: name.trim() || undefined,
      relationship_start: startDate || undefined,
    });
    if (!parsed.success) {
      for (const i of parsed.error.issues) {
        if (i.path[0] === "name") setNameError(i.message);
        if (i.path[0] === "relationship_start") setDateError(i.message);
      }
      return;
    }
    if (parsed.data.name === undefined && parsed.data.relationship_start === undefined) {
      setNameError("請至少修改一個欄位");
      return;
    }
    updateMut.mutate(parsed.data);
  };

  // Both early returns are wrapped in the container for the same gutter reason,
  // and both deliberately omit `data-testid="pair-settings-page"`.
  //
  // That testid is not only an identity marker — `compliance-settings.test.tsx`
  // awaits it as the signal that the async pair read has settled and the real
  // chrome has landed. Putting it on these branches made that await resolve on
  // the loading state, and the two W35.5 hero assertions then ran against a
  // branch that has no `PageHero` at all. The loading state used to be the
  // widest screen on the route; the not-found state still is, and the wrapper
  // fixes that without moving the marker.
  if (pairQuery.isLoading) {
    return (
      <PageContainer as="div" vertical="compact">
        <Card>
          <div className="p-5 text-body text-ink-2">讀取中…</div>
        </Card>
      </PageContainer>
    );
  }
  // W54.0 B2 — the two conditions this branch used to merge are two
  // different facts. `isError` is 「we could not read it」; `!data` is 「there
  // is nothing there」. Merged, a 500 told the person their pair did not
  // exist — and then told them to go and make one, which is the opposite of
  // the truth and a step in the wrong direction for someone who already has
  // one.
  if (pairQuery.isError) {
    return (
      <PageContainer as="div" vertical="compact">
        <Card>
          <ErrorState
            description={INFO_BANNER_PAIR_LOAD_FAILED}
            onRetry={() => void pairQuery.refetch()}
          />
        </Card>
      </PageContainer>
    );
  }
  if (!pairQuery.data) {
    // The fact, and only the fact: 「找不到」. The old copy also said 「請先建立
    // 配對」 and carried a 「去配對」 button, which bound a *fact* to a
    // *decision* — not-found is something that happened, creating a pair is
    // something the person may or may not want, and the two are not the same
    // thing.
    return (
      <PageContainer as="div" vertical="compact">
        <Card>
          <div className="p-5">
            <p className="text-body text-partner-strong">找不到這個 Pair。</p>
          </div>
        </Card>
      </PageContainer>
    );
  }

  const pair = pairQuery.data.pair;

  return (
    <div data-testid="pair-settings-page" className="flex flex-col">
      <PageHero
          // W52.0 D-9 — ADR-0115 named this page as 「PairSettings: none at
          // all」. It is the parent of `/settings/pair/dissolve`, so the trail
          // here is also the shape the child uses one level down.
          crumbs={[
            { to: "/settings", label: "設定", testId: "pair-settings-back" },
            { label: "空間設定" },
          ]}
          eyebrow="我們的空間"
          title="空間設定"
          subtitle={`Pair ${pair.name}`}
        />
      <PageContainer as="div">
        <Card>
          <form onSubmit={submit} noValidate className="p-6 flex flex-col gap-4">
            <FormErrorBanner message={null} />
            <FormField
              name="name"
              label="情侶名稱"
              required
              minLength={1}
              maxLength={50}
              value={name}
              onChange={setName}
              invalid={Boolean(nameError)}
              errorMessage={nameError ?? undefined}
              disabled={updateMut.isPending}
              data-testid="pair-name"
            />
            <FormField
              name="relationship_start"
              label="交往紀念日"
              type="date"
              required
              value={startDate}
              onChange={setStartDate}
              invalid={Boolean(dateError)}
              errorMessage={dateError ?? undefined}
              disabled={updateMut.isPending}
              data-testid="pair-start"
            />
            <div className="flex gap-3 justify-end pt-2">
              <Button
                type="button"
                variant="destructive"
                onClick={() => nav("/settings/pair/dissolve")}
                data-testid="dissolve-link"
              >
                解除配對
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={updateMut.isPending}
                data-testid="pair-save"
                leadingIcon={
                  updateMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null
                }
              >
                {updateMut.isPending ? "更新中…" : "儲存"}
              </Button>
            </div>
          </form>
        </Card>
      </PageContainer>
    </div>
  );
}
